/**
 * The aside: the searches nu-assist knows the person through (latest total
 * each), the relationship at a glance, and — on the standalone page only — the
 * way back into each list the person is on.
 *
 * "Searches in nu-assist" is NOT every search the person is in. It is derived
 * from the lists they appear in, so a Thrive search nobody built a list for is
 * missing (Jose, 2026-09-22). The heading and its hint say so. A second card,
 * "Searches in Thrive", fed by Thrive's candidacies, can sit right under it
 * later without either card having to change its words.
 */
import { Link } from "react-router-dom";

import type { PersonDetail, PersonRelationshipResponse } from "@nu-assist/contracts";

import { ScoreBadge } from "../../routes/research/import-detail/ScoreBadge";
import {
  RELATIONSHIP_EMPTY,
  RELATIONSHIP_UNAVAILABLE,
  isNuSyncUnavailable,
  isRelationshipEmpty,
} from "./RelationshipTab";
import {
  daysAgo,
  fmtDate,
  fmtDaysAgo,
  personFirstName,
  searchColours,
  searchKey,
  searchLabel,
  sourceLabel,
} from "./format";
import { currentAppearance, personListUrl } from "./actionContext";
import { PanelPager, usePagedList } from "./paging";
import { type PersonTab, personInListPath } from "./personHandle";

export const SEARCHES_IN_NU_ASSIST = "Searches in nu-assist";
export function searchesInNuAssistHint(first: string): string {
  return `From the lists ${first} appears in. Not every search in Thrive.`;
}

function AsideCard({
  title,
  hint,
  children,
  testId,
}: { title: string; hint?: string; children: React.ReactNode; testId?: string }) {
  return (
    <section data-testid={testId} className="rounded-lg border border-border bg-surface p-4">
      <h3 className={`text-label text-text-secondary ${hint ? "" : "mb-2"}`}>{title}</h3>
      {hint && (
        <p className="mb-2 text-caption text-text-muted" data-testid={testId && `${testId}-hint`}>
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

interface SearchRow {
  key: string;
  label: string;
  /** The newest list the person is in for this search — where "Open that
   *  list" goes. */
  latestImportId: string;
  latestListAt: string;
  lists: number;
  by: Set<string>;
  latestTotal: number | null;
  latestAt: string;
}

function searchRows(person: PersonDetail): SearchRow[] {
  const rows = new Map<string, SearchRow>();
  for (const a of person.appearances ?? []) {
    const key = searchKey(a);
    const row = rows.get(key) ?? {
      key,
      label: searchLabel(a),
      lists: 0,
      by: new Set(),
      latestTotal: null,
      latestAt: "",
      latestImportId: a.import_id,
      latestListAt: a.created_at,
    };
    row.lists += 1;
    if (a.created_at > row.latestListAt) {
      row.latestListAt = a.created_at;
      row.latestImportId = a.import_id;
    }
    row.by.add(a.uploaded_by);
    for (const r of a.ai_reviews ?? []) {
      if (r.created_at > row.latestAt) {
        row.latestAt = r.created_at;
        row.latestTotal = r.total ?? null;
      }
    }
    rows.set(key, row);
  }
  return [...rows.values()];
}

/** The latest total as a 1/2/3 badge, rounded the way the table's score
 *  legend reads, with the exact value beside it. */
function TotalBadge({ total }: { total: number | null }) {
  if (total == null) return <span className="text-caption text-text-muted">—</span>;
  return (
    <span
      className="flex items-center gap-1"
      title={`Latest AI review total ${total.toFixed(2)} / 3`}
    >
      <ScoreBadge value={Math.min(3, Math.max(1, Math.round(total)))} kind="score" />
      <span className="text-caption tabular-nums text-text-secondary">{total.toFixed(1)}</span>
    </span>
  );
}

export function PersonAside({
  person,
  relationship,
  relationshipError,
  relationshipLoading,
  mode,
  tab,
  currentImportId = null,
}: {
  person: PersonDetail;
  relationship: PersonRelationshipResponse | undefined;
  relationshipError: unknown;
  relationshipLoading: boolean;
  mode: "list" | "page";
  tab: PersonTab;
  /** The list the panel is open over (null on the standalone page): its
   *  search is the one Compare and Add act on, marked "this list". */
  currentImportId?: string | null;
}) {
  const colours = searchColours(person);
  const first = personFirstName(person);
  const summary = relationship?.summary;
  const owner = relationship?.contacts.find((c) => c.owner?.name)?.owner?.name;
  const lists = usePagedList(person.appearances ?? [], person.handle);
  const searches = usePagedList(searchRows(person), person.handle);
  const current = currentAppearance(person, currentImportId);
  const currentKey = current ? searchKey(current) : null;

  return (
    <aside className="flex flex-col gap-4" aria-label="About this person">
      {mode === "page" && (
        <AsideCard title="Open in a list" testId="person-open-in-list">
          <ul className="flex flex-col gap-2">
            {lists.items.map((a) => (
              <li key={a.import_id}>
                <Link
                  to={personInListPath(a.import_id, person.handle, tab)}
                  data-testid="person-open-in-list-link"
                  className="flex items-start gap-2 rounded-md p-1 text-body text-text hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${colours.get(searchKey(a))?.dot ?? "bg-chart-neutral"}`}
                  />
                  <span className="min-w-0">
                    <span className="block text-accent-ink">{a.list_name}</span>
                    <span className="block text-caption text-text-muted">
                      {sourceLabel(a.source)} · {a.uploaded_by} · {fmtDate(a.created_at)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <PanelPager paged={lists} noun="list" testId="person-open-in-list-pager" />
        </AsideCard>
      )}

      <AsideCard
        title={SEARCHES_IN_NU_ASSIST}
        hint={searchesInNuAssistHint(first)}
        testId="person-aside-searches"
      >
        <ul className="flex flex-col gap-2">
          {searches.items.map((s) => (
            <li key={s.key} className="flex items-start gap-2" data-testid="person-aside-search">
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${colours.get(s.key)?.dot ?? "bg-chart-neutral"}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 text-body text-text">
                  {s.label}
                  {s.key === currentKey && (
                    <span
                      className="rounded-pill bg-accent-muted px-2 text-micro text-accent-ink"
                      data-testid="person-aside-search-this-list"
                    >
                      this list
                    </span>
                  )}
                </div>
                <div className="text-caption text-text-muted">
                  {s.lists} list{s.lists === 1 ? "" : "s"} · {[...s.by].join(", ")}
                </div>
                {s.key !== currentKey && (
                  <Link
                    to={personListUrl(s.latestImportId, person.handle, tab)}
                    data-testid="person-aside-search-open"
                    className="text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    Open that list to act on it
                  </Link>
                )}
              </div>
              <TotalBadge total={s.latestTotal} />
            </li>
          ))}
        </ul>
        <PanelPager
          paged={searches}
          noun="search"
          nounPlural="searches"
          testId="person-aside-searches-pager"
        />
      </AsideCard>

      <AsideCard title="Relationship" testId="person-aside-relationship">
        {relationshipLoading ? (
          <p className="text-caption text-text-muted">Loading…</p>
        ) : relationshipError || !relationship ? (
          <p className="text-caption text-text-secondary">
            {isNuSyncUnavailable(relationshipError)
              ? RELATIONSHIP_UNAVAILABLE
              : "Could not load the relationship."}
          </p>
        ) : isRelationshipEmpty(relationship) ? (
          <p className="text-caption text-text-secondary">{RELATIONSHIP_EMPTY}</p>
        ) : (
          <dl className="grid grid-cols-2 gap-3 text-body">
            <div>
              <dt className="text-caption text-text-muted">Last outreach</dt>
              <dd className="text-text">
                {summary?.last_outreach_at ? fmtDaysAgo(daysAgo(summary.last_outreach_at)) : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-text-muted">Replied</dt>
              <dd className="text-text">
                {summary?.last_reply_at
                  ? `Yes · ${fmtDate(summary.last_reply_at)}`
                  : "No reply on record"}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-text-muted">Owner in Thrive</dt>
              <dd className="text-text">{owner ?? "Unassigned"}</dd>
            </div>
            <div>
              <dt className="text-caption text-text-muted">Touches</dt>
              <dd className="tabular-nums text-text">
                {summary?.outreaches ?? 0} outreaches · {summary?.notes ?? 0} notes
              </dd>
            </div>
          </dl>
        )}
      </AsideCard>
    </aside>
  );
}
